<template>
  <div style="overflow-x: auto; overflow-y: hidden; padding: 14px; margin: 6px 0">
    <div v-if="items && items.length > 0">
      <chart-sorter v-model="sorting" :sorting-items="sortingItems" />

      <div class="chart-container" :style="{ width: width }">
        <bar-chart ref="barChart" :chart-data="chartData" :options="chartOptions" />
      </div>
    </div>
    <chart-empty-state v-else />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import BarChart from '@/components/BarChart.vue'
import { localeString } from '@/config/helpers.js'
import { splitUnitName, filterUnit, removeCommonElement } from '@/modules/industria/helpers/chartHelper.js'
import ChartSorter from './ChartSorter.vue'
import ChartEmptyState from '@/components/campaigns/ChartEmptyState.vue'

export default {
  name: 'SoldProductsChart',
  components: {
    BarChart,
    ChartSorter,
    ChartEmptyState,
  },
  props: {
    selectedUnits: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default(_) {
        return []
      },
    },
    offerType: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    sorting: 'uv-desc',
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: (value, index, values) => {
                return localeString(value)
              },
            },
          },
        ],
        xAxes: [
          {
            gridLines: {
              display: false,
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem, data) => {
            return tooltipItem[0].xLabel.join(' ')
          },
          label: (tooltipItem, data) => {
            return `${data.datasets[tooltipItem.datasetIndex].label}: ${localeString(tooltipItem.yLabel)}`
          },
        },
      },
      layout: {
        padding: {
          top: 30,
        },
      },
      plugins: {
        datalabels: {
          clamp: true,
          anchor: 'end',
          align: 'top',
          offset: 0,
          formatter: (x) => localeString(x),
        },
      },
    },
  }),
  computed: {
    chartData() {
      let itemsClone = cloneDeep(this.items)
      const units = []
      const soldProducts = []
      const eligibleProducts = []
      const freeProducts = []
      const discountProducts = []
      const soldProductsWithVoucher = []

      switch (this.sorting) {
        case 'uv-asc':
          itemsClone.sort((a, b) => a.sold_products - b.sold_products)
          break
        case 'uv-desc':
          itemsClone.sort((a, b) => b.sold_products - a.sold_products)
          break
        case 'ue-asc':
          itemsClone.sort((a, b) => a.eligible_products - b.eligible_products)
          break
        case 'ue-desc':
          itemsClone.sort((a, b) => b.eligible_products - a.eligible_products)
          break
        case 'ug-asc':
          itemsClone.sort((a, b) => a.free_products - b.free_products)
          break
        case 'ug-desc':
          itemsClone.sort((a, b) => b.free_products - a.free_products)
          break
        case 'none':
        default:
          itemsClone = cloneDeep(this.items)
      }

      itemsClone = filterUnit(itemsClone, this.selectedUnits)
      itemsClone.forEach((item) => {
        units.push(splitUnitName(item.unit_name))
        soldProducts.push(item.sold_products)
        eligibleProducts.push(item.eligible_products)

        if (this.isCrossSell || this.isPercentageDiscount) {
          discountProducts.push(item.free_products)
        } else if (this.isTakePay) {
          freeProducts.push(item.free_products)
        } else if (this.isZeDelivery) {
          soldProductsWithVoucher.push(item.free_products)
        }
      })

      let datasets = [
        {
          label: this.isZeDelivery ? 'Unidades vendidas - Total' : 'Unidades vendidas',
          data: soldProducts,
          backgroundColor: '#002040',
        },
        {
          label: this.isZeDelivery ? 'Unidades vendidas com Voucher' : 'Unidades elegíveis',
          data: this.isZeDelivery ? soldProductsWithVoucher : eligibleProducts,
          backgroundColor: '#00B395',
        },
      ]

      if (this.isCrossSell || this.isPercentageDiscount) {
        datasets.push({
          label: 'Unidades com desconto',
          data: discountProducts,
          backgroundColor: '#94B300',
        })
      } else if (this.isTakePay) {
        datasets.push({
          label: 'Unidades gratuitas',
          data: freeProducts,
          backgroundColor: '#94B300',
        })
      }

      removeCommonElement(units)
      return {
        labels: units,
        datasets,
      }
    },

    sortingItems() {
      const sortingItems = [
        {
          sortBy: 'uv',
          name: this.isZeDelivery ? 'Unidades vendidas - Total' : 'Unidades vendidas',
          legendColor: '#002040',
        },
        {
          sortBy: this.isZeDelivery ? 'ug' : 'ue',
          name: this.isZeDelivery ? 'Unidades vendidas com Voucher' : 'Unidades elegíveis',
          legendColor: '#00b395',
        },
      ]

      if (this.isTakePay) {
        sortingItems.push({
          sortBy: 'ug',
          name: this.isCrossSell || this.isPercentageDiscount ? 'Unidades com desconto' : 'Unidades gratuitas',
          legendColor: '#94b300',
        })
      }
      return sortingItems
    },

    width() {
      return this.chartData.datasets[0].data.length * 100 + 'px'
    },

    isTakePay() {
      return this.offerType === 'takepay'
    },

    isCrossSell() {
      return this.offerType === 'crosssell'
    },

    isPercentageDiscount() {
      return this.offerType === 'percentage'
    },

    isZeDelivery() {
      return this.offerType === 'benefits'
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-container {
  position: relative;
  min-width: 100%;
}
</style>
